import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import axios from "axios";

const viewTypes = [
  { id: "presentation", name: "Presentation" },
  { id: "estimate", name: "Estimate" },
  { id: "salesOrder", name: "Sales Order" },
  { id: "depositInvoice", name: "Deposit Invoice" },
  { id: "shippingLabel", name: "Shipping Label" },
  { id: "packingSlip", name: "Packing Slip" },
  { id: "purchaseOrder", name: "Purchase Order" },
  { id: "feedback", name: "Feedback" },
];

const toDraftString = (value) => (value === null || value === undefined ? "" : String(value));

const buildViewConfigs = (defaults = {}) => {
  const headerDefaults = defaults.header || {};
  const footerDefaults = defaults.footer || {};
  return viewTypes.reduce((acc, view) => {
    acc[view.id] = {
      header: {
        logoUrl: toDraftString(headerDefaults.logoUrl),
        backgroundUrl: toDraftString(headerDefaults.backgroundUrl),
        backgroundColor: toDraftString(headerDefaults.backgroundColor),
        companyName: toDraftString(headerDefaults.companyName),
      },
      footer: {
        repType: toDraftString(footerDefaults.repType),
        addressId: toDraftString(footerDefaults.addressId),
        termsHtml: toDraftString(footerDefaults.termsHtml),
      },
    };
    return acc;
  }, {});
};

const normalizeViewConfigs = (raw, template) => {
  const defaults = {
    header: {
      logoUrl: toDraftString(template?.headerLogoUrl),
      backgroundUrl: toDraftString(template?.headerBackgroundUrl),
      backgroundColor: toDraftString(template?.headerBackgroundColor),
      companyName: toDraftString(template?.headerCompanyName),
    },
    footer: {
      repType: toDraftString(template?.footerRepType),
      addressId: toDraftString(template?.footerAddressId),
      termsHtml: toDraftString(template?.footerTermsHtml),
    },
  };

  const baseConfigs = buildViewConfigs(defaults);

  if (!raw || typeof raw !== "object") {
    return baseConfigs;
  }

  return viewTypes.reduce((acc, view) => {
    const viewConfig = raw?.[view.id] || {};
    const header = viewConfig.header || {};
    const footer = viewConfig.footer || {};
    acc[view.id] = {
      header: {
        logoUrl:
          header.logoUrl === undefined ? baseConfigs[view.id].header.logoUrl : toDraftString(header.logoUrl),
        backgroundUrl:
          header.backgroundUrl === undefined
            ? baseConfigs[view.id].header.backgroundUrl
            : toDraftString(header.backgroundUrl),
        backgroundColor:
          header.backgroundColor === undefined
            ? baseConfigs[view.id].header.backgroundColor
            : toDraftString(header.backgroundColor),
        companyName:
          header.companyName === undefined
            ? baseConfigs[view.id].header.companyName
            : toDraftString(header.companyName),
      },
      footer: {
        repType:
          footer.repType === undefined
            ? baseConfigs[view.id].footer.repType
            : toDraftString(footer.repType),
        addressId:
          footer.addressId === undefined
            ? baseConfigs[view.id].footer.addressId
            : toDraftString(footer.addressId),
        termsHtml:
          footer.termsHtml === undefined
            ? baseConfigs[view.id].footer.termsHtml
            : toDraftString(footer.termsHtml),
      },
    };
    return acc;
  }, {});
};

const defaultDraft = {
  id: null,
  name: "",
  viewConfigs: buildViewConfigs(),
};

const repTypes = [
  { id: "order-rep", name: "Order Rep" },
  { id: "client-rep", name: "Client Rep" },
  { id: "production-rep", name: "Production Rep" },
  { id: "finance-rep", name: "Finance Rep" },
];

export default function ProjectTemplatesTab({ instanceUniqueId }) {
  const [templates, setTemplates] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(defaultDraft);
  const [instanceId, setInstanceId] = useState(null);
  const [activeView, setActiveView] = useState("presentation");

  const SERVER = process.env.REACT_APP_NODEJS_SERVER_URL;
  const headers = { headers: { accessToken: localStorage.getItem("accessToken") } };

  const fetchInstanceId = async () => {
    if (!instanceUniqueId) return;
    const res = await axios.get(
      `${SERVER}/instances/byinstanceUniqueId/${instanceUniqueId}`,
      headers
    );
    setInstanceId(res.data?.id || null);
  };

  const fetchTemplates = async (targetInstanceId = instanceId) => {
    if (!targetInstanceId) return;
    const res = await axios.get(
      `${SERVER}/instances/${targetInstanceId}/project-templates`,
      headers
    );
    setTemplates(res.data.templates || []);
    setAddresses(res.data.addresses || []);
  };

  useEffect(() => {
    fetchInstanceId();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanceUniqueId]);

  useEffect(() => {
    if (instanceId) fetchTemplates(instanceId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanceId]);

  const openCreate = () => {
    setDraft(defaultDraft);
    setActiveView("presentation");
    setOpen(true);
  };

  const openEdit = (template) => {
    setDraft({
      id: template.id,
      name: template.name || "",
      viewConfigs: normalizeViewConfigs(template.viewConfigs, template),
    });
    setActiveView("presentation");
    setOpen(true);
  };

  const save = async () => {
    if (!instanceId) return;
    const normalizedViewConfigs = viewTypes.reduce((acc, view) => {
      const viewConfig = draft.viewConfigs?.[view.id] || {};
      const header = viewConfig.header || {};
      const footer = viewConfig.footer || {};
      acc[view.id] = {
        header: {
          logoUrl: header.logoUrl?.trim() || null,
          backgroundUrl: header.backgroundUrl?.trim() || null,
          backgroundColor: header.backgroundColor?.trim() || null,
          companyName: header.companyName?.trim() || null,
        },
        footer: {
          repType: footer.repType || null,
          addressId: footer.addressId ? Number(footer.addressId) : null,
          termsHtml: footer.termsHtml?.trim() || null,
        },
      };
      return acc;
    }, {});
    const presentationConfig = normalizedViewConfigs.presentation || {};
    const payload = {
      name: draft.name.trim(),
      viewConfigs: normalizedViewConfigs,
      headerLogoUrl: presentationConfig.header?.logoUrl || null,
      headerBackgroundUrl: presentationConfig.header?.backgroundUrl || null,
      headerBackgroundColor: presentationConfig.header?.backgroundColor || null,
      headerCompanyName: presentationConfig.header?.companyName || null,
      footerRepType: presentationConfig.footer?.repType || null,
      footerAddressId: presentationConfig.footer?.addressId ?? null,
    };
    if (!payload.name) return;

    if (draft.id) {
      await axios.put(
        `${SERVER}/instances/${instanceId}/project-templates/${draft.id}`,
        payload,
        headers
      );
    } else {
      await axios.post(
        `${SERVER}/instances/${instanceId}/project-templates`,
        payload,
        headers
      );
    }
    setOpen(false);
    fetchTemplates(instanceId);
  };

  const remove = async (template) => {
    if (!instanceId) return;
    await axios.delete(
      `${SERVER}/instances/${instanceId}/project-templates/${template.id}`,
      headers
    );
    fetchTemplates(instanceId);
  };

  const canSave = useMemo(() => draft.name.trim().length > 0, [draft]);
  const activeConfig = draft.viewConfigs?.[activeView] || buildViewConfigs()[activeView];
  const activeRepLabel =
    repTypes.find((rep) => rep.id === activeConfig.footer?.repType)?.name || "Rep Name";
  const activeAddress = addresses.find(
    (address) => String(address.id) === String(activeConfig.footer?.addressId)
  );
  const addressSummary = activeAddress
    ? [
        activeAddress.companyName,
        activeAddress.addressLine1,
        activeAddress.addressLine2,
        [activeAddress.city, activeAddress.stateProvince, activeAddress.postalCode]
          .filter(Boolean)
          .join(", "),
        activeAddress.country,
      ]
        .filter(Boolean)
        .join(" · ")
    : "Address";

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            Project Templates
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            Manage header and footer defaults for project documents.
          </Typography>
        </Box>

        <Button variant="contained" onClick={openCreate}>
          Create Template
        </Button>
      </Box>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Header</TableCell>
            <TableCell>Footer</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
      <TableBody>
          {(templates || []).map((template) => {
            const resolvedConfigs = normalizeViewConfigs(template.viewConfigs, template);
            const presentation = resolvedConfigs.presentation || {};
            const header = presentation.header || {};
            const footer = presentation.footer || {};
            const hasHeader =
              !!header.logoUrl ||
              !!header.backgroundUrl ||
              !!header.backgroundColor ||
              !!header.companyName;
            const hasFooter = !!footer.repType || !!footer.addressId;

            return (
              <TableRow key={template.id} hover>
                <TableCell sx={{ fontWeight: 700 }}>{template.name}</TableCell>
                <TableCell>{hasHeader ? "Configured" : "—"}</TableCell>
                <TableCell>{hasFooter ? "Configured" : "—"}</TableCell>
                <TableCell align="right">
                  <IconButton onClick={() => openEdit(template)} size="small">
                    <EditOutlinedIcon fontSize="small" />
                  </IconButton>
                  <IconButton onClick={() => remove(template)} size="small">
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            );
          })}
          {(templates || []).length === 0 && (
            <TableRow>
              <TableCell colSpan={4} sx={{ py: 4, textAlign: "center", opacity: 0.7 }}>
                No templates yet. Create your first one.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>
          {draft.id ? "Edit Template" : "Create Template"}
        </DialogTitle>
        <DialogContent sx={{ display: "grid", gap: 2, pt: 1 }}>
          <TextField
            label="Template Name"
            value={draft.name}
            onChange={(e) => setDraft((prev) => ({ ...prev, name: e.target.value }))}
          />

          <Box>
            <Tabs
              value={activeView}
              onChange={(_, value) => setActiveView(value)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ borderBottom: "1px solid", borderColor: "divider" }}
            >
              {viewTypes.map((view) => (
                <Tab key={view.id} value={view.id} label={view.name} />
              ))}
            </Tabs>
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                Header
              </Typography>
              <Box sx={{ display: "grid", gap: 1.5 }}>
                <TextField
                  label="Logo URL"
                  value={activeConfig.header?.logoUrl || ""}
                  onChange={(e) =>
                    setDraft((prev) => ({
                      ...prev,
                      viewConfigs: {
                        ...prev.viewConfigs,
                        [activeView]: {
                          ...prev.viewConfigs[activeView],
                          header: {
                            ...prev.viewConfigs[activeView].header,
                            logoUrl: e.target.value,
                          },
                        },
                      },
                    }))
                  }
                  placeholder="https://"
                />
                <TextField
                  label="Background Image URL"
                  value={activeConfig.header?.backgroundUrl || ""}
                  onChange={(e) =>
                    setDraft((prev) => ({
                      ...prev,
                      viewConfigs: {
                        ...prev.viewConfigs,
                        [activeView]: {
                          ...prev.viewConfigs[activeView],
                          header: {
                            ...prev.viewConfigs[activeView].header,
                            backgroundUrl: e.target.value,
                          },
                        },
                      },
                    }))
                  }
                  placeholder="https://"
                />
                <TextField
                  label="Background Color"
                  value={activeConfig.header?.backgroundColor || ""}
                  onChange={(e) =>
                    setDraft((prev) => ({
                      ...prev,
                      viewConfigs: {
                        ...prev.viewConfigs,
                        [activeView]: {
                          ...prev.viewConfigs[activeView],
                          header: {
                            ...prev.viewConfigs[activeView].header,
                            backgroundColor: e.target.value,
                          },
                        },
                      },
                    }))
                  }
                  placeholder="#0F172A"
                />
                <TextField
                  label="Company Name"
                  value={activeConfig.header?.companyName || ""}
                  onChange={(e) =>
                    setDraft((prev) => ({
                      ...prev,
                      viewConfigs: {
                        ...prev.viewConfigs,
                        [activeView]: {
                          ...prev.viewConfigs[activeView],
                          header: {
                            ...prev.viewConfigs[activeView].header,
                            companyName: e.target.value,
                          },
                        },
                      },
                    }))
                  }
                />
              </Box>
            </Box>

            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                Footer
              </Typography>
              <Box sx={{ display: "grid", gap: 1.5 }}>
                <TextField
                  label="Rep Type"
                  select
                  value={activeConfig.footer?.repType || ""}
                  onChange={(e) =>
                    setDraft((prev) => ({
                      ...prev,
                      viewConfigs: {
                        ...prev.viewConfigs,
                        [activeView]: {
                          ...prev.viewConfigs[activeView],
                          footer: {
                            ...prev.viewConfigs[activeView].footer,
                            repType: e.target.value,
                          },
                        },
                      },
                    }))
                  }
                >
                  <MenuItem value="">None</MenuItem>
                  {repTypes.map((rep) => (
                    <MenuItem key={rep.id} value={rep.id}>
                      {rep.name}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  label="Company Address"
                  select
                  value={activeConfig.footer?.addressId || ""}
                  onChange={(e) =>
                    setDraft((prev) => ({
                      ...prev,
                      viewConfigs: {
                        ...prev.viewConfigs,
                        [activeView]: {
                          ...prev.viewConfigs[activeView],
                          footer: {
                            ...prev.viewConfigs[activeView].footer,
                            addressId: e.target.value,
                          },
                        },
                      },
                    }))
                  }
                >
                  <MenuItem value="">None</MenuItem>
                  {addresses.map((address) => (
                    <MenuItem key={address.id} value={address.id}>
                      {address.label}
                    </MenuItem>
                  ))}
                </TextField>
                {activeView !== "presentation" && (
                  <TextField
                    label="Terms & Conditions (HTML)"
                    value={activeConfig.footer?.termsHtml || ""}
                    onChange={(e) =>
                      setDraft((prev) => ({
                        ...prev,
                        viewConfigs: {
                          ...prev.viewConfigs,
                          [activeView]: {
                            ...prev.viewConfigs[activeView],
                            footer: {
                              ...prev.viewConfigs[activeView].footer,
                              termsHtml: e.target.value,
                            },
                          },
                        },
                      }))
                    }
                    multiline
                    minRows={4}
                    placeholder="<h3>Terms and Conditions</h3><p>...</p>"
                  />
                )}
              </Box>
            </Box>

            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                Live Preview
              </Typography>
              <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    px: 2,
                    py: 1.5,
                    minHeight: 70,
                    backgroundColor: activeConfig.header?.backgroundUrl
                      ? "transparent"
                      : activeConfig.header?.backgroundColor || "#111827",
                    backgroundImage: activeConfig.header?.backgroundUrl
                      ? `url(${activeConfig.header.backgroundUrl})`
                      : "none",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    color: "#fff",
                  }}
                >
                  <Box
                    sx={{
                      width: 64,
                      height: 40,
                      borderRadius: 1,
                      overflow: "hidden",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "rgba(255,255,255,0.2)",
                    }}
                  >
                    {activeConfig.header?.logoUrl ? (
                      <Box
                        component="img"
                        src={activeConfig.header.logoUrl}
                        alt="Logo preview"
                        sx={{ maxWidth: "100%", maxHeight: "100%" }}
                      />
                    ) : (
                      <Typography variant="caption">Logo</Typography>
                    )}
                  </Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {activeConfig.header?.companyName || "Company Name"}
                  </Typography>
                </Box>
                <Box sx={{ height: 50, backgroundColor: "#F3F4F6" }} />
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    px: 2,
                    py: 1.5,
                    backgroundColor: "#fff",
                  }}
                >
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                      {activeRepLabel}
                    </Typography>
                    <Typography variant="caption" sx={{ display: "block", opacity: 0.7 }}>
                      {addressSummary}
                    </Typography>
                  </Box>
                  <Typography variant="caption" sx={{ opacity: 0.6 }}>
                    Footer
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={!canSave}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
